import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  enablePushNotifications,
  isPushSupported,
} from "src/config/pushNotifications";
import {
  shouldShowPushPrompt,
  persistPushPromptAccepted,
  persistPushPromptDismissed,
} from "src/utils/pushPermissionStore";
import { upsertUserFcmToken } from "src/config/pushNotificationsBackend";
import { isAuthenticated } from "src/Funcs/auth/authStore";
import DashNotifsSVG from "src/Images/Dashboard/DashNotifsSVG";
import CloseSVG from "src/Images/Board/CloseSVG";

const INITIAL_DELAY_MS = 30_000;

const PUBLIC_ROUTES = ["/", "/Register", "/pricing"];

export default function PushNotificationPrompt() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRTL = lang === "fa" || lang === "ar";
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [loading, setLoading] = useState(false);

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  useEffect(() => {
    if (isPublicRoute) return;
    if (shouldShowPushPrompt()) {
      const timer = setTimeout(() => setVisible(true), INITIAL_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [isPublicRoute]);

  const handleAccept = useCallback(async () => {
    setLoading(true);
    try {
      const supported = await isPushSupported();
      if (!supported) {
        toast.info(t("pushNotSupported"));
        dismissBanner();
        return;
      }
      const res = await enablePushNotifications({
        onToken: async (token) => {
          localStorage.setItem("fcmToken", token);
          if (isAuthenticated()) {
            await upsertUserFcmToken(token);
            localStorage.setItem("fcmTokenLastSynced", token);
          }
        },
      });
      if (res?.token) {
        persistPushPromptAccepted();
        dismissBanner();
        toast.success(t("pushEnabled"));
      } else {
        if (Notification.permission !== "granted") {
          dismissBanner();
        } else {
          toast.error(t("pushEnableFailed"));
        }
      }
    } catch (e) {
      toast.error(e?.message || t("pushEnableFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const dismissBanner = useCallback(() => {
    setExiting(true);
    persistPushPromptDismissed();
    setTimeout(() => setVisible(false), 300);
  }, []);

  if (!visible || isPublicRoute) return null;

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`fixed top-0 left-0 right-0 z-[99990] flex justify-center px-3 sm:px-4 pt-2.5 sm:pt-3 pointer-events-none ${
        exiting ? "animate-slideUp" : "animate-slideDown"
      }`}
      role="dialog"
      aria-label={t("pushPromptTitle")}
      aria-modal="false"
    >
      <div
        className="pointer-events-auto w-full max-w-xl bg-[var(--white)] rounded-2xl border border-[var(--purple)]/15 shadow-2xl"
        style={{
          boxShadow:
            "0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(111,56,197,0.04)",
        }}
      >
        <div className="flex gap-3 sm:gap-4 p-4 sm:p-5">
          <div className="flex-1 min-w-0">
            <DashNotifsSVG width={44} height={44} fill="var(--purple)" />

            <h3 className="text-lg sm:text-base font-bold text-[var(--black)] m-0 leading-snug">
              {t("pushPromptTitle")}
            </h3>
            <p className="text-xs sm:text-sm text-[var(--lessDark)] mt-1 sm:mt-1.5 leading-relaxed">
              {t("pushPromptDescription")}
            </p>

            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 mt-3 sm:mt-4">
              <button
                type="button"
                disabled={loading}
                onClick={handleAccept}
                className="flex items-center justify-center gap-1.5 px-16 py-2.5 sm:py-3 rounded-xl bg-[var(--purple)] text-white text-sm font-semibold hover:brightness-110 active:scale-[0.97] transition-all duration-200 disabled:opacity-50 disabled:cursor-wait min-w-[100px]"
              >
                {loading && (
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {t("pushAllow")}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={dismissBanner}
                className="px-5 py-2.5 sm:py-3 rounded-xl border border-[var(--lessDark)]/20 text-[var(--lessDark)] text-sm font-medium hover:bg-[var(--lightGray)] active:scale-[0.97] transition-all duration-200 disabled:opacity-40"
              >
                {t("pushNotYet")}
              </button>
            </div>
          </div>

          <div
            disabled={loading}
            onClick={dismissBanner}
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 disabled:opacity-30 -mr-1 -mt-0.5"
            aria-label={t("Close")}
          >
            <CloseSVG width="14" height="14" fill="var(--lessDark)" />
          </div>
        </div>
      </div>
    </div>
  );
}
